import "./styles.css";
import { Col, Row, Select } from "antd";
import { useEffect, useState } from "react";
import { fleetVehicleMetaDataApiUrl, fleetsUrl } from "../../Common/endpoints";
import { getApis, postApis } from "../../Common/apis";
import { ORG_TYPES, ROLES } from "../../Common/Constants";
import { useRequiredAccess } from "../../../hooks/useRequiredAccess";
import { SmileOutlined } from "@ant-design/icons";
import DateTimePicker from "../../Common/DateTimePicker";
import Overview from "./Overview";
import FleetTable from "./FleetTable";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { setPageLoader } from "../../../reducers/loaderSlice";
import { useDispatch } from "react-redux";

const FleetAnalytics = () => {
  const dispatch = useDispatch();
  const [dropdownDetails, setDropdownDetails] = useState({
    fleetList: null,
    imeiList: [],
  });
  const [filterDetails, setFilterDetails] = useState({
    fleet: null,
    imei: ["all"],
    dateRange: [dayjs().subtract(10, "d").set("hour", 0).set("minute", 0).set("second", 0), dayjs()],
  });
  const [metaData, setMetaData] = useState({});
  const authToken = localStorage.getItem("authToken");
  const checkAccess = useRequiredAccess(
    [ROLES.ADMIN, ROLES.EXECUTIVE, ROLES.FLEET_MANAGER],
    [ORG_TYPES.ADMINISTRATOR, ORG_TYPES.MANUFACTURER]
  );

  const onFilterChange = (name, value) => {
    if (name === "fleet") {
      const imeiList =
        dropdownDetails?.fleetList.find((fleet) => fleet.fleetId === value)
          ?.vehicleImei || [];
      setDropdownDetails((prev) => ({ ...prev, imeiList }));
      setFilterDetails((prev) => ({
        ...prev,
        [name]: value,
        imei: imeiList?.[0] ? ["all"] : [],
      }));
    } else if (name === "imei") {
      const [firstImei, ...otherImei] = value;
      const selectedImei =
        !value?.length || otherImei.at(-1) === "all"
          ? ["all"]
          : firstImei === "all"
          ? otherImei
          : value;
      setFilterDetails((prev) => ({ ...prev, imei: selectedImei }));
    } else {
      setFilterDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onImeiDeselect = () => {
    if (
      filterDetails?.imei?.length <= 2 &&
      !filterDetails?.imei?.includes("all")
    ) {
      toast.error("Atleast two vehicle should be selected");
    }
  };

  const getFleetVehicleMetaData = async () => {
    if (
      filterDetails?.fleet &&
      (filterDetails?.imei?.length > 1 || filterDetails?.imei.includes("all"))
    ) {
      const data = {
        fleetId: filterDetails.fleet,
        from: Math.floor(filterDetails?.dateRange?.[0]),
        to: Math.floor(filterDetails?.dateRange?.[1]),
        imei: filterDetails.imei.filter((item) => item !== "all"),
      };
      await postApis(fleetVehicleMetaDataApiUrl, data, authToken)
        .then((response) => {
          console.log('1')
          setMetaData(response?.data || {});
          dispatch(setPageLoader(null));
        })
        .catch((error) => {
          setMetaData({});
          console.log(error);
          dispatch(setPageLoader(null));
        });
    }
  };

  const getFleets = async () => {
    await getApis(fleetsUrl, {}, authToken)
      .then((response) => {
        console.log('2')
        setFilterDetails({
          fleet: response?.data[0]?.fleetId,
          imei: response?.data[0]?.vehicleImei?.length > 0 ? ["all"] : [],
          dateRange: [dayjs().subtract(10, "d").set("hour", 0).set("minute", 0).set("second", 0), dayjs()],
        });
        setDropdownDetails({
          fleetList: response?.data,
          imeiList: response?.data[0]?.vehicleImei,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getFleetVehicleMetaData();
    // eslint-disable-next-line
  }, [filterDetails]);

  useEffect(() => {
    checkAccess();
    dispatch(setPageLoader("refresh"));
    getFleets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return dropdownDetails?.fleetList?.length > 0 ? (
    <Row className="fleet-statistics-page" data-testid="fleet-statistics">
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            {dropdownDetails?.fleetList?.length > 0 && (
              <Select
                showSearch
                value={
                  filterDetails?.fleet ||
                  dropdownDetails?.fleetList?.[0]?.fleetName
                }
                size="large"
                style={{ width: "100%" }}
                onChange={(value) => onFilterChange("fleet", value)}
                options={dropdownDetails?.fleetList.map((i) => ({
                  label: i.fleetName,
                  value: i.fleetId,
                }))}
              />
            )}
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <Select
              allowClear
              size="large"
              mode="multiple"
              placeholder="IMEI"
              className={
                "w100" +
                (!filterDetails?.imei?.includes("all") ? " imei-values" : "")
              }
              value={filterDetails?.imei}
              maxTagCount={"responsive"}
              status={
                filterDetails?.imei?.length === 1 &&
                !filterDetails?.imei.includes("all")
                  ? "error"
                  : null
              }
              onDeselect={onImeiDeselect}
              onChange={(value) => onFilterChange("imei", value)}
            >
              {dropdownDetails?.imeiList?.length > 0 && (
                <Select.Option value="all">All</Select.Option>
              )}
              {dropdownDetails?.imeiList?.map((value, i) => (
                <Select.Option key={i} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={8} lg={12} xl={14} className="flex-end">
            <DateTimePicker
              dates={filterDetails?.dateRange}
              onChangePicker={(value) => onFilterChange("dateRange", value)}
              disableRange={3}
              disableRangeType={"M"}
              extraFilter={true}
            />
          </Col>
        </Row>

        <Overview metaData={metaData} />

        <Row gutter={[16, 16]} className="mt1">
          <Col span={24} className="table-title">
            Fleet:
          </Col>
          <Col span={24}>
            <FleetTable filterDetails={filterDetails} />
          </Col>
        </Row>
      </Col>
    </Row>
  ) : dropdownDetails?.fleetList ? (
    <Row
      justify="center"
      align={"middle"}
      className="w100 hv25 flex-center fleet-statistic-page"
      data-testid="no-fleet-statistics"
    >
      <Col span={24} className="text-center">
        <div>
          <SmileOutlined
            className="icon-font32 mb05 text-center"
            style={{ color: "#667085" }}
          />
        </div>
        <div className="grey f13">No Fleet Statistics</div>
      </Col>
    </Row>
  ) : null;
};

export default FleetAnalytics;
